import React, { useEffect, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { academyAdminApi } from '../../services/api';
import { contentApi } from '../../services/contentApi';
import { API_PATHS, apiHeaders, apiUrl } from '../../config/api';
import { EnrollmentReportRow } from '../../types/content';
import CourseFee from '../../components/UI/CourseFee';
import Button from '../../components/UI/Button';
import { confirmAction } from '../../utils/confirmAction';
import { formatDate } from '../../utils/formatDate';

const monthKey = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (key: string) => {
  if (!key) return 'Unknown';
  const [year, month] = key.split('-').map(Number);
  const date = new Date(year, (month || 1) - 1, 1);
  return date.toLocaleString(undefined, { month: 'short', year: 'numeric' });
};

const formatMoney = (amount: number) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const ONBOARDING_HELP = [
  { value: 'Self', meaning: 'Learner enrolled themselves (paid or trial fee path).' },
  { value: 'Free', meaning: 'Zero-payable enrollment (trial / free course).' },
  { value: 'AdminCode', meaning: 'Redeemed an admin invite / access code (skips payment).' },
  { value: 'InterestQueue', meaning: 'Owner/CourseAdmin enrolled them from the interest waitlist.' },
];

const statusLabel = (row: EnrollmentReportRow) => {
  if (row.isCourseCompleted) return 'Completed';
  return row.status || 'Unknown';
};

const ReportsAdminPage: React.FC = () => {
  const [rows, setRows] = useState<EnrollmentReportRow[]>([]);
  const [mobiles, setMobiles] = useState<Record<number, string>>({});
  const [programs, setPrograms] = useState<Record<number, { courseTitle?: string; status: string }[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [monthFilter, setMonthFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [showOnboardHelp, setShowOnboardHelp] = useState(false);

  const load = async () => {
    try {
      const r = await contentApi.enrollmentReport();
      const list = r.data.rows || [];
      setRows(list);
      const ids = [...new Set(list.map((item) => item.userProfileId).filter((id) => id > 0))];
      if (!ids.length) {
        setPrograms({});
        setMobiles({});
        return;
      }

      const [academy, profilesResponse] = await Promise.all([
        academyAdminApi.enrollmentsByUsers(ids).catch(() => null),
        fetch(apiUrl(API_PATHS.userProfile.byIds), {
          method: 'POST',
          headers: { ...apiHeaders(localStorage.getItem('token')), 'Content-Type': 'application/json' },
          body: JSON.stringify({ userProfileIds: ids }),
        }).then((res) => res.json()).catch(() => null),
      ]);

      const map: Record<number, { courseTitle?: string; status: string }[]> = {};
      for (const user of academy?.data?.users || []) {
        map[user.userProfileId] = (user.enrolledCourses || []).map((course) => ({
          courseTitle: course.courseTitle,
          status: course.status,
        }));
      }
      setPrograms(map);

      const mobileMap: Record<number, string> = {};
      const profiles = profilesResponse?.userProfiles || [];
      for (const profile of profiles as Array<{ id?: number; mobile?: string }>) {
        const id = Number(profile.id);
        const mobile = String(profile.mobile || '').trim();
        if (id > 0 && mobile) mobileMap[id] = mobile;
      }
      setMobiles(mobileMap);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load the report.');
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const monthOptions = useMemo(() => {
    const keys = [...new Set(rows.map((row) => monthKey(row.dateCreated)).filter(Boolean))];
    return keys.sort((a, b) => b.localeCompare(a));
  }, [rows]);

  const courseOptions = useMemo(() => {
    const titles = [...new Set(rows.map((row) => row.courseTitle).filter(Boolean))];
    return titles.sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const statusOptions = useMemo(() => {
    const values = [...new Set(rows.map((row) => statusLabel(row)).filter(Boolean))];
    return values.sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => rows.filter((row) => {
    if (monthFilter && monthKey(row.dateCreated) !== monthFilter) return false;
    if (courseFilter && row.courseTitle !== courseFilter) return false;
    if (statusFilter && statusLabel(row) !== statusFilter) return false;
    return true;
  }), [rows, monthFilter, courseFilter, statusFilter]);

  const summary = useMemo(() => {
    const paidRows = filteredRows.filter((r) => r.isPaid && !/^cancelled$/i.test(r.status));
    const unpaidRows = filteredRows.filter((r) => !r.isPaid && !/^cancelled$/i.test(r.status));
    return {
      total: filteredRows.length,
      admin: filteredRows.filter((r) => r.onboardingSource === 'AdminCode').length,
      paid: paidRows.length,
      completed: filteredRows.filter((r) => r.isCourseCompleted).length,
      feesReceived: paidRows.reduce((sum, row) => sum + (row.payable || 0), 0),
      feesProspect: unpaidRows.reduce((sum, row) => sum + (row.payable || 0), 0),
    };
  }, [filteredRows]);

  const cancelRegistration = async (row: EnrollmentReportRow) => {
    if (/^cancelled$/i.test(row.status)) return;
    const ok = await confirmAction({
      title: 'Cancel registration?',
      description: `Cancel ${row.userEmail || `profile #${row.userProfileId}`} on “${row.courseTitle}”? Status becomes Cancelled.`,
      confirmLabel: 'Cancel registration',
      variant: 'danger',
    });
    if (!ok) return;
    setCancellingId(row.enrollmentId);
    try {
      const response = await contentApi.cancelCourseEnrollment(row.enrollmentId);
      if (!response.data.success) {
        setError(response.data.message || 'Could not cancel registration.');
        return;
      }
      setRows((prev) => prev.map((item) => (
        item.enrollmentId === row.enrollmentId
          ? { ...item, status: 'Cancelled' }
          : item
      )));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not cancel registration.');
    } finally {
      setCancellingId(null);
    }
  };

  if (error && rows.length === 0) return <p className="text-gray-600">{error}</p>;

  return (
    <div>
      <p className="text-gray-600 mb-4">How learners were onboarded, pricing captured at enrollment, and linked IDC Academy program registrations.</p>
      {error && <p className="mb-3 text-sm text-error-600">{error}</p>}

      <div className="mb-4 flex flex-wrap gap-3">
        <label className="text-sm text-gray-600">
          Month
          <select
            className="ml-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
          >
            <option value="">All months</option>
            {monthOptions.map((key) => (
              <option key={key} value={key}>{monthLabel(key)}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-600">
          Course
          <select
            className="ml-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
            value={courseFilter}
            onChange={(e) => setCourseFilter(e.target.value)}
          >
            <option value="">All courses</option>
            {courseOptions.map((title) => (
              <option key={title} value={title}>{title}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-600">
          Status
          <select
            className="ml-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <div className="rounded-xl bg-white border p-3"><p className="text-xs text-gray-500">Enrollments</p><p className="text-xl font-semibold">{summary.total}</p></div>
        <div className="rounded-xl bg-white border p-3"><p className="text-xs text-gray-500">Admin codes</p><p className="text-xl font-semibold">{summary.admin}</p></div>
        <div className="rounded-xl bg-white border p-3"><p className="text-xs text-gray-500">Paid / free</p><p className="text-xl font-semibold">{summary.paid}</p></div>
        <div className="rounded-xl bg-white border p-3"><p className="text-xs text-gray-500">Completed</p><p className="text-xl font-semibold">{summary.completed}</p></div>
        <div className="rounded-xl bg-white border border-primary-100 p-3"><p className="text-xs text-primary-700">Fees received</p><p className="text-xl font-semibold text-primary-900">{formatMoney(summary.feesReceived)}</p></div>
        <div className="rounded-xl bg-white border border-amber-100 p-3"><p className="text-xs text-amber-700">Fees (prospect)</p><p className="text-xl font-semibold text-amber-900">{formatMoney(summary.feesProspect)}</p></div>
      </div>
      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="px-3 py-2">Learner</th>
              <th className="px-3 py-2">Mobile</th>
              <th className="px-3 py-2">Course</th>
              <th className="px-3 py-2">
                <span className="inline-flex items-center gap-1">
                  Onboarded
                  <button
                    type="button"
                    className="rounded p-0.5 text-gray-400 hover:text-primary-700"
                    aria-label="Explain onboarded values"
                    onClick={() => setShowOnboardHelp((open) => !open)}
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </span>
              </th>
              <th className="px-3 py-2">Fee</th>
              <th className="px-3 py-2">Interview sessions</th>
              <th className="px-3 py-2">Registered</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">IDC Academy</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {showOnboardHelp && (
              <tr className="border-t bg-primary-50/40">
                <td colSpan={10} className="px-3 py-3 text-xs text-gray-700">
                  <p className="font-semibold text-gray-800 mb-1">Onboarded values</p>
                  <ul className="space-y-1">
                    {ONBOARDING_HELP.map((item) => (
                      <li key={item.value}><span className="font-medium">{item.value}</span> — {item.meaning}</li>
                    ))}
                  </ul>
                </td>
              </tr>
            )}
            {filteredRows.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-6 text-gray-500">No course enrollments yet.</td></tr>
            )}
            {filteredRows.map((row) => {
              const unpaidPending = !row.isPaid && /^pendingpayment$/i.test(row.status);
              const cancelled = /^cancelled$/i.test(row.status);
              const displayStatus = statusLabel(row);
              return (
                <tr key={row.enrollmentId} className="border-t align-top">
                  <td className="px-3 py-2">{row.userEmail || row.userProfileId}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{mobiles[row.userProfileId] || '—'}</td>
                  <td className="px-3 py-2">{row.courseTitle}</td>
                  <td className="px-3 py-2">{row.onboardingSource}</td>
                  <td className={`px-3 py-2 ${unpaidPending ? 'text-red-600 font-semibold' : ''}`}>
                    <CourseFee fee={row.price} discount={row.discountPercent} />
                    {unpaidPending ? <span className="block text-xs font-medium">PendingPayment · unpaid</span> : null}
                    {!row.isPaid && !unpaidPending && !cancelled ? <span className="block text-xs text-amber-700">unpaid</span> : null}
                  </td>
                  <td className="px-3 py-2">{row.interviewPackageCount ?? 0}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{row.dateCreated ? formatDate(row.dateCreated) : '—'}</td>
                  <td className={`px-3 py-2 ${unpaidPending ? 'text-red-600 font-medium' : cancelled ? 'text-gray-500' : ''}`}>
                    {displayStatus}{unpaidPending ? ' · unpaid' : ''}
                  </td>
                  <td className="px-3 py-2">
                    {(programs[row.userProfileId] || []).length === 0
                      ? <span className="text-gray-400">None</span>
                      : programs[row.userProfileId].map((p) => `${p.courseTitle || 'Program'} (${p.status})`).join(', ')}
                  </td>
                  <td className="px-3 py-2">
                    {cancelled ? (
                      <span className="text-xs text-gray-400">Cancelled</span>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={cancellingId === row.enrollmentId}
                        onClick={() => void cancelRegistration(row)}
                      >
                        {cancellingId === row.enrollmentId ? 'Cancelling…' : 'Cancel'}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReportsAdminPage;
